import React, { FC, useMemo, useState, useEffect } from 'react';
import parse from 'html-react-parser';
import { Link, useNavigate } from 'react-router-dom';

import {
  trackSampleProjectOpen,
  trackHomeClickViewAllProjects,
  trackHomeProjectsTabChange,
} from '../../../analytics';

import { PageWrapper } from '../../../components/layout/content';

import { URL, SAMPLE_ID } from '../../../constants';

import useAuthUserStore from '../../../hooks/useAuthUserStore';
import useSampleProjectData from '../../../hooks/useSampleProjectData';

import { useProjectStore } from '../../../stores/project';

import { Event } from '../../../types';
import { isEmpty, toTitleCase } from '../../../utils';

import ProjectsContainer from '../components/projectsContainer';
import ProjectsTab from '../components/projectsTab';
import CreateProjectBtn from './components/createProjectBtn';
import LearningMaterials from './components/learningMaterials';
import OnboardingVideoModal from './components/onboardingVideoModal';
import UserGuidingModal from './components/userGuidingModal';

const Home: FC = () => {
  const { user } = useAuthUserStore();
  const { userEvents, clearEventId } = useProjectStore();

  const [pageTab, setPageTab] = useState('recent');
  const [showOnboardingVideo, setShowOnboardingVideo] = useState(false);
  const [showUGModal, setShowUGModal] = useState(false);

  // sample project data
  const { sampleProjectData, resetSampleProjectData } = useSampleProjectData();

  const navigate = useNavigate();

  const { isNewUser, recentProjects, completedProjects } = useMemo(() => {
    let isNewUser = false;
    const recentProjects: Event[] = [];
    const completedProjects: Event[] = [];

    userEvents.forEach((event: Event) => {
      if (event.stage === 'generation') completedProjects.push(event);
      else recentProjects.push(event);
    });

    if (isEmpty(recentProjects))
      if (isEmpty(completedProjects)) isNewUser = true;
      else setPageTab('completed');

    return { isNewUser, recentProjects, completedProjects };
  }, [userEvents]);

  useEffect(() => {
    clearEventId();
  }, [clearEventId]);

  useEffect(() => {
    if (isNewUser && !user.role) setShowUGModal(true);
  }, [isNewUser, user.role]);

  const handleOpenSampleProject = (isLink?: boolean) => {
    if (isEmpty(sampleProjectData)) resetSampleProjectData();

    trackSampleProjectOpen();

    if (!isLink) navigate(`/${SAMPLE_ID.FIFA}${URL.VIDEO_BUILDER}`);
  };

  const tabs = [
    {
      name: 'recent',
      projects: recentProjects,
      emptyMsg: 'Your projects will appear here after creating one',
    },
    {
      name: 'completed',
      projects: completedProjects,
      emptyMsg: 'Completed projects will appear here after generating videos',
    },
  ];

  const selectedTab = useMemo(() => {
    return tabs.find((tab) => tab.name === pageTab);
  }, [pageTab]);

  const MAX_PROJECTS = 10;

  const learningMaterials = (
    <LearningMaterials
      setShowUGModal={setShowUGModal}
      setShowOnboardingVideo={setShowOnboardingVideo}
      handleOpenSampleProject={handleOpenSampleProject}
    />
  );

  const renderContent = () => {
    return (
      <div>
        <div className="flex flex-wrap items-center gap-4">
          {tabs.map((tab) => (
            <ProjectsTab
              key={tab.name}
              label={`${tab.name} projects`}
              onClick={() => {
                setPageTab(tab.name);
                trackHomeProjectsTabChange(tab.name);
              }}
              isCurrent={pageTab === tab.name}
            />
          ))}

          <Link
            to={URL.DASH_PROJECTS}
            onClick={() => trackHomeClickViewAllProjects('Main')}
            className="hidden sm:block text-sm no-underline"
          >
            View All
          </Link>
        </div>

        {!isEmpty(selectedTab?.projects) ? (
          <ProjectsContainer
            heading={toTitleCase(`${selectedTab?.name} Projects`)}
            events={selectedTab?.projects ?? []}
            projectNumLimit={MAX_PROJECTS}
            hideHeading
          />
        ) : (
          <div className="mt-4 text-sm text-grey-900">
            {selectedTab?.emptyMsg ?? ''}
          </div>
        )}
      </div>
    );
  };

  return (
    <PageWrapper type="user-dashboard" title="Home" className="p-0">
      {/* welcome box */}
      <div className="animated-box-purple w-full flex flex-col items-center justify-center text-center px-[5vw] py-4 lg:px-8 lg:py-6 gap-4 lg:gap-6 text-white lg:rounded-t-md animate-running-5">
        <div className="text-xl sm:text-2xl font-semibold">
          Welcome {isNewUser ? parse('to CrowdClip&reg;') : 'Back'},{' '}
          {user.firstName ?? 'amazing human'}!
        </div>

        <div>
          Let's get started on a better way to generate hundreds of personalised
          videos for your events.
        </div>

        <CreateProjectBtn />

        <Link
          to={`/${SAMPLE_ID.FIFA}${URL.VIDEO_BUILDER}`}
          onClick={() => handleOpenSampleProject(true)}
          className="text-white"
        >
          Play with Sample Project
        </Link>
      </div>

      {/* content */}
      <div className="w-full flex flex-col self-center px-[5vw] py-4 lg:p-8 lg:pt-4 gap-8">
        {isNewUser ? (
          <React.Fragment>
            {learningMaterials}

            <h5>Projects</h5>

            <div className="w-full text-grey-900 font-bold text-center">
              Your projects will appear here after creating one
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            {renderContent()}

            {learningMaterials}
          </React.Fragment>
        )}
      </div>

      <OnboardingVideoModal
        isNewUser={false}
        showOnboardingVideo={showOnboardingVideo}
        setShowOnboardingVideo={setShowOnboardingVideo}
      />

      <UserGuidingModal
        show={showUGModal}
        onHide={() => setShowUGModal(false)}
        user={user}
      />
    </PageWrapper>
  );
};

export default Home;
